import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trash2, Plus, Mail } from 'lucide-react';
import { toast } from 'sonner';

const NOTIFY_FIELDS = [
  ['receive_weekly', 'Weekly reports'],
  ['receive_monthly', 'Monthly reports'],
  ['receive_quarterly', 'Quarterly reports'],
  ['receive_ai_alerts', 'AI visibility alerts'],
  ['receive_competitor_alerts', 'Competitor alerts'],
  ['receive_review_alerts', 'Review alerts'],
  ['receive_publishing', 'Publishing notifications'],
  ['receive_billing', 'Billing notifications'],
];

export default function Settings() {
  const { activeClient } = useClient();
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(blank());

  const load = async () => {
    if (!activeClient) return;
    const { data } = await api.get(`/clients/${activeClient.client_id}/contacts`);
    setContacts(data || []);
  };
  useEffect(() => { load(); }, [activeClient]);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;

  const addContact = async () => {
    if (!form.email || !form.first_name) return toast.error('First name + email required');
    await api.post(`/clients/${activeClient.client_id}/contacts`, form);
    setForm(blank());
    await load();
    toast.success('Contact added');
  };

  const removeContact = async (id) => {
    await api.delete(`/contacts/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Settings</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Contacts & notifications</h1>
      </div>

      <Tabs defaultValue="contacts">
        <TabsList>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="whitelabel">White Label</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="mt-6 space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="text-sm font-medium mb-4">Add contact</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              <Input placeholder="First name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
              <Input placeholder="Last name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              <Input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              <Input placeholder="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <Input placeholder="Department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} />
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {NOTIFY_FIELDS.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <Label htmlFor={k} className="text-sm">{label}</Label>
                  <Switch id={k} checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} />
                </div>
              ))}
            </div>
            <Button className="mt-4" onClick={addContact}><Plus className="h-4 w-4 mr-1" /> Add contact</Button>
          </div>

          <div className="space-y-2">
            {contacts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No contacts yet.
              </div>
            ) : (
              contacts.map(c => (
                <div key={c.contact_id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-medium">{c.first_name} {c.last_name} <span className="text-muted-foreground font-normal">· {c.title}</span></div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {NOTIFY_FIELDS.filter(([k]) => c[k]).map(([k, label]) => (
                        <span key={k} className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">{label}</span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeContact(c.contact_id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Billing</div>
            <div className="mt-4 font-display text-2xl">Stripe integration · coming next</div>
            <div className="mt-2 text-sm text-muted-foreground">Monthly + annual plans, usage-based billing, agency rollups.</div>
          </div>
        </TabsContent>

        <TabsContent value="whitelabel" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">White Label</div>
            <div className="mt-4 font-display text-2xl">Agency mode · enterprise tier</div>
            <div className="mt-2 text-sm text-muted-foreground">Custom branding, domains, dashboards, agency billing.</div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function blank() {
  return {
    first_name: '', last_name: '', title: '', email: '', phone: '', department: '',
    receive_weekly: true, receive_monthly: true, receive_quarterly: true,
    receive_ai_alerts: true, receive_competitor_alerts: true,
    receive_review_alerts: false, receive_publishing: false, receive_billing: false,
  };
}
